import { ChevronLeft, ChevronRight, RefreshCw, Search } from "lucide-react";
import { useState } from "react";
import useSWR from "swr";
import { swrFetcher } from "@/web/api";
import { PageHeader } from "@/web/components/page-header";
import { Badge } from "@/web/components/ui/badge";
import { Button } from "@/web/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/web/components/ui/card";
import { Input } from "@/web/components/ui/input";
import { Loading } from "@/web/components/ui/loading";
import { formatDate } from "@/web/lib/utils";

interface LogRow {
  id: number;
  actor: string;
  action: string;
  target_type: string | null;
  target_id: string | null;
  ip: string;
  details_json: string;
  created_at: string;
}

interface LogResponse {
  data: LogRow[];
  total: number;
  limit: number;
  offset: number;
}

const SCOPES = [
  { value: "", label: "全部" },
  { value: "payment.", label: "支付 / 上报" },
  { value: "order.", label: "订单操作" },
  { value: "auth.", label: "登录" },
  { value: "settings.", label: "配置变更" },
  { value: "upstream.", label: "上游通道" },
];

const PAGE_SIZE = 60;

/** 事件名 → 颜色：失败类红色，支付/订单类主色，其余描边。 */
function actionVariant(action: string): "danger" | "primary" | "outline" {
  if (/failed|error|denied|rejected/i.test(action)) return "danger";
  if (action.startsWith("payment.") || action.startsWith("order.")) return "primary";
  return "outline";
}

/** 详情里最值得一眼看到的字段（上报类事件）。 */
function summarize(detailsJson: string): string {
  try {
    const details = JSON.parse(detailsJson) as Record<string, unknown>;
    const parts: string[] = [];
    if (details.money) parts.push(`金额 ${String(details.money)}`);
    if (details.recognized_cents !== undefined) parts.push(`识别 ${String(details.recognized_cents)} 分`);
    if (details.text) parts.push(`原文「${String(details.text).slice(0, 60)}」`);
    if (details.out_trade_no) parts.push(String(details.out_trade_no));
    if (details.trade_no) parts.push(String(details.trade_no));
    if (details.keys) parts.push(`字段 ${String(details.keys)}`);
    return parts.join(" · ");
  } catch {
    return "";
  }
}

export function LogsPage() {
  const [scope, setScope] = useState("");
  const [keyword, setKeyword] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [expanded, setExpanded] = useState<number | null>(null);

  const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(page * PAGE_SIZE) });
  if (scope) params.set("scope", scope);
  if (search) params.set("q", search);
  const key = `/admin-api/logs?${params.toString()}`;

  const { data, isLoading, mutate } = useSWR<LogResponse>(key, swrFetcher, { refreshInterval: 15_000 });

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  return (
    <>
      <PageHeader title="日志" description="订单、支付与上报、登录、配置变更等全部业务事件。上报记录里能看到收到的原文和识别出的金额。" />

      <Card>
        <CardHeader>
          <CardTitle>筛选</CardTitle>
          <CardDescription>共 {data?.total ?? 0} 条记录，每页 {PAGE_SIZE} 条。</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {SCOPES.map((item) => (
              <button
                key={item.value}
                type="button"
                onClick={() => { setScope(item.value); setPage(0); }}
                className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                  scope === item.value ? "border-primary bg-primary/10 text-primary" : "text-muted hover:bg-foreground/[0.04]"
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>
          <form
            className="flex flex-wrap items-center gap-2"
            onSubmit={(event) => { event.preventDefault(); setSearch(keyword.trim()); setPage(0); }}
          >
            <Input
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜事件名、订单号、原文、IP…"
              className="min-w-0 flex-1"
            />
            <Button type="submit" variant="outline"><Search />搜索</Button>
            <Button type="button" variant="outline" onClick={() => { setKeyword(""); setSearch(""); setScope(""); setPage(0); }}>重置</Button>
            <Button type="button" variant="outline" onClick={() => void mutate()}><RefreshCw />刷新</Button>
          </form>
        </CardContent>
      </Card>

      <Card className="mt-6">
        <CardContent className="pt-6">
          {isLoading && !data ? (
            <Loading label="正在读取日志" />
          ) : !data?.data.length ? (
            <div className="py-12 text-center text-sm text-muted">没有匹配的日志</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[860px] text-left text-sm">
                <thead className="border-b text-xs text-muted">
                  <tr>
                    <th className="pb-3 font-medium">时间</th>
                    <th className="pb-3 font-medium">事件</th>
                    <th className="pb-3 font-medium">对象</th>
                    <th className="pb-3 font-medium">来源</th>
                    <th className="pb-3 font-medium">详情</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {data.data.map((row) => (
                    <tr
                      key={row.id}
                      className="align-top cursor-pointer hover:bg-foreground/[0.02]"
                      onClick={() => setExpanded(expanded === row.id ? null : row.id)}
                    >
                      <td className="whitespace-nowrap py-3 text-xs">{formatDate(row.created_at)}</td>
                      <td className="py-3"><Badge variant={actionVariant(row.action)}>{row.action}</Badge></td>
                      <td className="py-3 font-mono text-xs text-muted">{row.target_id ?? "—"}</td>
                      <td className="py-3 font-mono text-xs text-muted">{row.actor}{row.ip ? ` / ${row.ip}` : ""}</td>
                      <td className="py-3 text-xs text-muted">
                        {expanded === row.id ? (
                          <pre className="max-w-[36rem] overflow-x-auto whitespace-pre-wrap break-all rounded bg-foreground/[0.04] p-2 font-mono text-[11px] leading-5">
                            {(() => { try { return JSON.stringify(JSON.parse(row.details_json), null, 2); } catch { return row.details_json; } })()}
                          </pre>
                        ) : (
                          <span>{summarize(row.details_json) || "点击展开"}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="mt-5 flex items-center justify-between gap-3">
            <span className="text-xs text-muted">第 {page + 1} / {totalPages} 页</span>
            <div className="flex gap-2">
              <Button type="button" variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((value) => Math.max(0, value - 1))}>
                <ChevronLeft />上一页
              </Button>
              <Button type="button" variant="outline" size="sm" disabled={page + 1 >= totalPages} onClick={() => setPage((value) => value + 1)}>
                下一页<ChevronRight />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </>
  );
}
